import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutGrid, ArrowRight, ShieldCheck, Search, X, type LucideIcon } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { Badge, Skeleton, TooltipProvider } from '@vnetwork/ui/primitives';
import { useMyServices, useRecordServiceAccess, serviceProviderScope, type AvailableService } from '@/api/services';
import { brand } from '@/config/brand';
import {
  brandServiceCategories,
  categoryOfScope,
  serviceRoute,
  iconForScope,
  catalogPath,
  productByScope,
  type ServiceCategory,
  type ServiceProduct,
} from './categories';
import { serviceIconForScope } from './service-icons';
import { HelpTooltip } from './components/HelpTooltip';
import { groupCDNServices, type CDNServiceGroup } from './cdn-service-grouping';
import { isWaapCapable, waapConsoleRoute, WAAP_PRODUCT, waapServiceLabel } from './waap/waap-service';
import { isWafCapable, wafConsoleRoute, WAF_PRODUCT } from './waf/waf-service';
import { formatDate, toneColor } from './shared';
import styles from './Services.module.css';

/** Card icon slot — the product's own glyph when mapped, else the generic
 *  category icon in a tinted tile (fallback). Matches the mock's 36×35 artwork. */
function CardLogo({
  src,
  fallback: Fallback,
  active,
}: {
  src?: string;
  fallback: LucideIcon;
  active?: boolean;
}) {
  if (src) return <img src={src} alt="" className={styles.cardLogo} />;
  return (
    <span className={`${styles.cardIcon}${active ? ` ${styles.cardIconActive}` : ''}`}>
      <Fallback size={19} />
    </span>
  );
}

/** Card title with an info affordance — the name plus the shared hover popover
 *  carrying the product's one-line explanation and a docs link (same component the
 *  Overview dashboard uses, so both read identically). */
function CardName({ name, tip }: { name: string; tip?: string }) {
  return (
    <span className={styles.cardName}>
      <span className={styles.cardTitle}>{name}</span>
      {tip && <HelpTooltip title={name} description={tip} docsHref={brand.urls.docs} />}
    </span>
  );
}

/** The 3-up stat summary — one bordered box with 1px cell dividers (mock). */
export function ServiceStatRow({
  items,
}: {
  items: { label: string; value: number; tone: 'success' | 'info' | 'warning' }[];
}) {
  return (
    <div className={styles.statRow}>
      {items.map((s) => (
        <div key={s.label} className={styles.statCell}>
          <div className={styles.statLabel}>
            <span className={styles.statDot} style={{ background: toneColor(s.tone) }} />
            {s.label}
          </div>
          <div className={styles.statValue}>{s.value}</div>
        </div>
      ))}
    </div>
  );
}

export function ProvisionedCard({
  service,
  providerCount,
  asWaap = false,
  asWaf = false,
}: {
  service: AvailableService;
  /** Present when this card stands in for a grouped Multi-CDN umbrella. */
  providerCount?: number;
  /** Render this service as its standalone WAAP console card (separate service). */
  asWaap?: boolean;
  /** Render this service as the Owl/Mlytics WAF console card (under Security). */
  asWaf?: boolean;
}) {
  const navigate = useNavigate();
  const recordAccess = useRecordServiceAccess();
  const scope = serviceProviderScope(service);
  const isCdnGroup = providerCount != null;
  // CDN umbrella card → CDN home (orchestrator list); individual services keep
  // their scoped route. WAAP and WAF are surfaced as their own console cards.
  const route = asWaap
    ? waapConsoleRoute(service.id)
    : asWaf
      ? wafConsoleRoute(service.id)
      : isCdnGroup
        ? '/services/cdn/home'
        : serviceRoute(scope, service.id);
  const Icon = asWaap || asWaf ? ShieldCheck : iconForScope(scope);
  const iconScope = asWaap ? 'Security.WAAP' : asWaf ? 'Security.WAF' : isCdnGroup ? 'External.Mlytics' : scope;
  const logo = serviceIconForScope(iconScope);
  const clickable = !!route;
  // Reuse the catalog product (by scope; CDN umbrellas → canonical Mlytics) so an
  // active card carries the same description + features as its catalog tile.
  const product = productByScope(isCdnGroup ? 'External.Mlytics' : scope);
  const description = asWaap ? WAAP_PRODUCT.description : asWaf ? WAF_PRODUCT.description : product?.description;
  const features: readonly string[] = asWaap
    ? WAAP_PRODUCT.features
    : asWaf
      ? WAF_PRODUCT.features
      : product?.features ?? [];
  // Never fall back to the raw scope string — scopes embed upstream vendor
  // names that must not surface in the UI.
  const title = asWaap
    ? // Full product name as the card title; the service's own name shows in the sub.
      WAAP_PRODUCT.serviceName
    : asWaf
      ? WAF_PRODUCT.displayName
      : isCdnGroup
        ? 'Multi-CDN Orchestration'
        : // Product name as the title; the service's own name shows in the sub.
          product?.displayName || service.service_name || service.provider?.display_name || 'Service';
  const sub = asWaap
    ? waapServiceLabel(service)
    : asWaf
      ? service.service_name || ''
      : isCdnGroup
        ? `${providerCount} provider${providerCount === 1 ? '' : 's'}`
        : service.service_name || service.provider?.display_name || '';
  // Drop the sub when it just echoes the title (e.g. provider name == service name).
  const showSub = !!sub && sub.toLowerCase() !== title.toLowerCase();
  const open = () => {
    if (!route) return;
    if (!isCdnGroup) recordAccess(service.id);
    navigate(route);
  };
  return (
    <div
      className={`${styles.card}${clickable ? ` ${styles.cardClickable}` : ''}`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={open}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) open();
      }}
    >
      <div className={styles.cardHead}>
        <CardLogo src={logo} fallback={Icon} active />
        <div style={{ minWidth: 0, flex: 1 }}>
          <CardName name={title} tip={description} />
          {showSub && <div className={styles.cardSub}>{sub}</div>}
        </div>
        <StatusPill tone="success">Active</StatusPill>
      </div>
      {features.length > 0 && (
        <div className={styles.features}>
          {features.slice(0, 4).map((f) => (
            <span key={f} className={styles.feature}>
              {f}
            </span>
          ))}
        </div>
      )}
      <div className={styles.cardFoot}>
        {/* formatDate returns '—' for missing/zero dates → omit the line entirely. */}
        <span className={styles.cardSub}>
          {(() => {
            const since = formatDate(service.created_at);
            return since !== '—' ? `Since ${since}` : '';
          })()}
        </span>
        {clickable ? (
          <span className={styles.openHint}>
            Open <ArrowRight size={14} />
          </span>
        ) : (
          <span className={styles.mutedHint}>Console coming soon</span>
        )}
      </div>
    </div>
  );
}

export function ProductCard({ product, category }: { product: ServiceProduct; category: ServiceCategory }) {
  const navigate = useNavigate();
  const Icon = category.icon;
  const coming = product.status === 'coming_soon';
  const tone = coming ? 'warning' : product.status === 'beta' ? 'info' : 'success';
  const label = coming ? 'Coming soon' : product.status === 'beta' ? 'Beta' : 'Available';
  const to = product.consoleRoute ?? catalogPath(category.key, product.name);
  const logo = serviceIconForScope(product.scope, coming);
  return (
    <div
      className={`${styles.card} ${styles.cardClickable}${coming ? ` ${styles.cardComing}` : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') navigate(to);
      }}
    >
      <div className={styles.cardHead}>
        <CardLogo src={logo} fallback={Icon} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <CardName name={product.displayName} tip={product.description} />
        </div>
        <Badge tone={tone} appearance="subtle" size="sm">
          {label}
        </Badge>
      </div>
      <div className={styles.cardDesc}>{product.description}</div>
      <div className={styles.features}>
        {product.features.slice(0, 4).map((f) => (
          <span key={f} className={styles.feature}>
            {f}
          </span>
        ))}
      </div>
      <div className={styles.cardFoot}>
        <span className={styles.cardSub}>{coming ? 'Learn more' : 'View details'}</span>
        <span className={styles.openHint}>
          Open <ArrowRight size={14} />
        </span>
      </div>
    </div>
  );
}

/** Status filter chips above the grid — mirrors the "All services" mock. */
type StatusFilter = 'all' | 'registered' | 'available' | 'coming';
const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'registered', label: 'Registered' },
  { key: 'available', label: 'Available' },
  // 'Coming soon' filter dropped — coming-soon products are hidden everywhere.
];

export default function ServicesDashboard() {
  const { data: services = [], isLoading } = useMyServices();

  // Category filter is URL-driven (?category=…) — selected from the nested
  // "Services" group in the app's primary sidebar. Unknown keys fall back to all.
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const enabledCategories = brandServiceCategories();
  const active =
    categoryParam && enabledCategories.some((c) => c.key === categoryParam)
      ? categoryParam
      : 'all';

  // CDN provider services collapse into one umbrella card per org (interim
  // client-side grouping until the backend owns `parent_service_id`).
  const cdnGroups = useMemo(() => groupCDNServices(services), [services]);
  const cdnGroupByCanonicalId = useMemo(() => {
    const m = new Map<string, CDNServiceGroup>();
    for (const g of cdnGroups) m.set(g.canonical.id, g);
    return m;
  }, [cdnGroups]);

  // WAAP is a standalone service ("1 service khác") surfaced under Security — one
  // card per WAAP-capable (eagle) service, routing to its own WAAP console.
  const waapServices = useMemo(() => services.filter(isWaapCapable), [services]);

  // Owl/Mlytics WAF services are also surfaced under Security (they open the
  // dedicated /services/waf console, not the shared CDN one).
  const wafServices = useMemo(() => services.filter(isWafCapable), [services]);

  const byCat = useMemo(() => {
    const m: Record<string, AvailableService[]> = {};
    for (const s of services) {
      if (isWaapCapable(s)) continue; // WAAP rendered as its own card under Security
      const key = categoryOfScope(serviceProviderScope(s));
      if (key === 'cdn') continue; // CDN rendered via the grouped umbrella below
      (m[key] ||= []).push(s);
    }
    if (cdnGroups.length) m['cdn'] = cdnGroups.map((g) => g.canonical);
    return m;
  }, [services, cdnGroups]);

  // Catalog dedup keys on the resolved product, not the raw scope — a provider
  // can surface under multiple backend scopes (e.g. Penguin/LMS =
  // Internal.HLSLowLatency or External.SwiftCoder.LMS) that map to one product.
  // WAF/WAAP services (Owl/Mlytics, Cobra) are surfaced as their own Security
  // cards and kept out of the Multi-CDN umbrella (see groupCDNServices); they
  // must likewise NOT mark a catalog product as owned — an Owl/Mlytics WAF
  // service shares scope External.Mlytics with the Multi-CDN Orchestration
  // product, so counting it here hides that card (and empties Networking & CDN)
  // in Mlytics-only orgs.
  const provProducts = useMemo(
    () =>
      new Set(
        services
          .filter((s) => !isWafCapable(s) && !isWaapCapable(s))
          .map((s) => productByScope(serviceProviderScope(s)))
          .filter(Boolean),
      ),
    [services],
  );

  const cats =
    active === 'all' ? enabledCategories : enabledCategories.filter((c) => c.key === active);

  // ── Search + status filter (client-side over the provisioned + catalog sets) ──
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const q = query.trim().toLowerCase();
  const showProvisioned = statusFilter === 'all' || statusFilter === 'registered';
  const serviceMatches = (s: AvailableService) => {
    if (!q) return true;
    const p = productByScope(serviceProviderScope(s));
    return [s.service_name, s.provider?.display_name, p?.displayName, p?.description, ...(p?.features ?? [])]
      .some((t) => t?.toLowerCase().includes(q));
  };
  const productMatches = (p: ServiceProduct) =>
    !q || [p.displayName, p.description, ...p.features].some((t) => t.toLowerCase().includes(q));
  // 'registered' shows only provisioned; 'available'/'coming' only catalog of that status.
  const catalogStatusOk = (p: ServiceProduct) =>
    statusFilter === 'all'
      ? true
      : statusFilter === 'available'
        ? p.status === 'active' || p.status === 'beta'
        : statusFilter === 'coming'
          ? p.status === 'coming_soon'
          : false;

  const sections = cats
    .map((cat) => {
      const provAll = byCat[cat.key] ?? [];
      // WAAP + WAF cards (standalone/dedicated consoles) live under Security.
      const waapAll = cat.key === 'security' ? waapServices : [];
      const wafAll = cat.key === 'security' ? wafServices : [];
      // CDN catalog product is hidden once the org holds any CDN service; likewise
      // drop the WAF/WAAP tiles once a matching service exists.
      const catalogAll = (
        cat.key === 'cdn' && cdnGroups.length > 0 ? [] : cat.products.filter((p) => !provProducts.has(p))
      )
        .filter((p) => !(cat.key === 'security' && wafAll.length > 0 && p.scope === 'Security.WAF'))
        .filter((p) => !(cat.key === 'security' && waapAll.length > 0 && p.scope === 'Security.WAAP'));
      const prov = showProvisioned ? provAll.filter(serviceMatches) : [];
      const waap = showProvisioned ? waapAll.filter(serviceMatches) : [];
      const waf = showProvisioned ? wafAll.filter(serviceMatches) : [];
      const catalog =
        statusFilter === 'registered' ? [] : catalogAll.filter((p) => catalogStatusOk(p) && productMatches(p));
      return { cat, prov, waap, waf, catalog };
    })
    .filter((s) => s.prov.length + s.waap.length + s.waf.length + s.catalog.length > 0);

  const otherServices =
    active === 'all' && showProvisioned ? (byCat['other'] ?? []).filter(serviceMatches) : [];

  const nothing = sections.length === 0 && otherServices.length === 0;
  const resetFilters = () => {
    setQuery('');
    setStatusFilter('all');
  };

  return (
    <TooltipProvider>
    <Container size="xl">
      <PageHeader
        icon={<LayoutGrid size={22} />}
        eyebrow="Services"
        title="All services"
        description="Your provisioned services and the products available across the platform."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Search size={15} color="var(--vn-fg-subtle)" />
          <input
            className={styles.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search services…"
            aria-label="Search services"
          />
          {query && (
            <button className={styles.clearBtn} onClick={() => setQuery('')} aria-label="Clear search">
              <X size={12} />
            </button>
          )}
        </div>
        <div className={styles.chips} role="tablist" aria-label="Filter by status">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={statusFilter === s.key}
              className={`${styles.chip}${statusFilter === s.key ? ` ${styles.chipActive}` : ''}`}
              onClick={() => setStatusFilter(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 132 }} />
          ))}
        </div>
      ) : nothing ? (
        <EmptyState
          icon={<Search size={26} />}
          title="No services match your filters"
          description="Try a different keyword or switch back to All."
          actions={
            <button className={styles.resetBtn} onClick={resetFilters}>
              Reset filters
            </button>
          }
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {sections.map(({ cat, prov, waap, waf, catalog }) => (
            <Section key={cat.key} title={cat.title} description={cat.description} style={{ marginBottom: 0 }}>
              <div className={styles.grid}>
                {prov.map((s) => (
                  <ProvisionedCard
                    key={s.id}
                    service={s}
                    providerCount={
                      cat.key === 'cdn' ? cdnGroupByCanonicalId.get(s.id)?.providerCount : undefined
                    }
                  />
                ))}
                {waap.map((s) => (
                  <ProvisionedCard key={`waap-${s.id}`} service={s} asWaap />
                ))}
                {waf.map((s) => (
                  <ProvisionedCard key={`waf-${s.id}`} service={s} asWaf />
                ))}
                {catalog.map((p) => (
                  <ProductCard key={p.scope} product={p} category={cat} />
                ))}
              </div>
            </Section>
          ))}

          {otherServices.length > 0 && (
            <Section
              title="Other services"
              description="Provisioned services not yet mapped to a console category."
              style={{ marginBottom: 0 }}
            >
              <div className={styles.grid}>
                {otherServices.map((s) => (
                  <ProvisionedCard key={s.id} service={s} />
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
      </div>

    </Container>
    </TooltipProvider>
  );
}
